import { useEffect } from 'react';
import { create } from 'zustand';
import type { PublicUser } from '@orbit-sync/shared-types';
import { api } from '../lib/api/index.js';

/**
 * The name cache: user id -> display name.
 *
 * `Message` carries a `senderId` and `Channel` a list of `memberIds`, so almost
 * every screen holds ids it needs to render as names. Rather than have each
 * component fetch its own, they declare which ids they need and this store
 * resolves the unknown ones.
 *
 * Two things make that cheap:
 *   - **Requests are batched.** Asks made in the same tick - one per message row,
 *     say - are collected and sent as a single `GET /users/lookup?ids=a,b,c`.
 *   - **Ids are fetched once.** Anything already known or already in flight is
 *     dropped from the next batch, so re-renders cost nothing.
 *
 * Names are not secret and do not change during a session, so entries are kept
 * until sign-out. `reset()` clears them, for the same reason the chat store is
 * cleared: one account's directory must not survive into the next one's session.
 */

interface DirectoryState {
  names: Record<string, PublicUser>;
  /** Ask for these ids. Fire-and-forget: the store re-renders when they land. */
  ensure: (ids: readonly string[]) => void;
  /** Seed names already in hand - e.g. search results - to skip a round trip. */
  remember: (users: readonly PublicUser[]) => void;
  reset: () => void;
}

/** Ids waiting for the next flush, and ids already requested from the server. */
const queued = new Set<string>();
const inFlight = new Set<string>();
let flushScheduled = false;

export const useDirectoryStore = create<DirectoryState>((set, get) => ({
  names: {},

  ensure(ids: readonly string[]): void {
    const known = get().names;
    let added = false;

    for (const id of ids) {
      if (id && !known[id] && !inFlight.has(id) && !queued.has(id)) {
        queued.add(id);
        added = true;
      }
    }

    if (!added || flushScheduled) {
      return;
    }

    flushScheduled = true;

    // A microtask, not a timer: everything rendered in this commit asks before
    // the browser paints, so one batch covers the whole screen.
    queueMicrotask(() => {
      flushScheduled = false;

      const batch = [...queued];
      queued.clear();

      if (batch.length === 0) {
        return;
      }

      for (const id of batch) {
        inFlight.add(id);
      }

      void api.users
        .lookupUsers(batch)
        .then((users) => {
          get().remember(users);
        })
        .catch(() => {
          // A name is a nicety, not a feature: on failure the UI keeps showing
          // the id fragment. Dropping these from `inFlight` lets a later render
          // retry rather than leaving them permanently unresolved.
        })
        .finally(() => {
          for (const id of batch) {
            inFlight.delete(id);
          }
        });
    });
  },

  remember(users: readonly PublicUser[]): void {
    if (users.length === 0) {
      return;
    }

    set((state) => {
      const names = { ...state.names };

      for (const user of users) {
        names[user.id] = user;
      }

      return { names };
    });
  },

  reset(): void {
    queued.clear();
    inFlight.clear();
    set({ names: {} });
  },
}));

/**
 * Declares the ids a component needs and returns the current name map.
 *
 * The map is the store's own object, so its identity is stable between renders -
 * returning a filtered copy would give React a new snapshot every time and
 * defeat memoisation.
 */
export function useDirectory(ids: readonly string[]): Record<string, PublicUser> {
  const names = useDirectoryStore((state) => state.names);
  const ensure = useDirectoryStore((state) => state.ensure);

  // Joined, because a fresh array literal from the caller would be a new
  // dependency on every render and re-run this effect forever.
  const key = ids.join(',');

  useEffect(() => {
    ensure(key.length > 0 ? key.split(',') : []);
  }, [key, ensure]);

  return names;
}
